import { z } from 'zod';

import { sectionBaseSchema } from '../base';
import { selectedFileSchemaLegacy } from '../../../widget/common/schemas/legacy';
import { colorValueSchema, numberWithUnitSchema, spacingSchema } from '../../../mixins';

/**
 * Which of the three fixed header slots the row draws. Named for the slots
 * rather than the legacy `showLeftColumn` flags — the row always has exactly
 * three, so "column" was never the right word for them.
 */
export const sectionSlotsSchema = z.object({
    left: z.boolean(),
    center: z.boolean(),
    right: z.boolean(),
});

/**
 * Background of the row. Flat, not responsive: a row has one background for
 * every screen — see `sectionDefaultDataSchema` for why the whole entity is.
 *
 * The picked file keeps its legacy shape on purpose: the
 * CSS-variable builders take the whole file (they need its resize flags and the
 * host's CDN base), so storing a bare id would only move that lookup around.
 */
export const sectionBackgroundSchema = z.object({
    color: colorValueSchema,
    image: selectedFileSchemaLegacy.nullable(),
    /** `null` leaves the CSS default — legacy wrote an empty string for it. */
    size: z.string().nullable(),
    position: z.string().nullable(),
    repeat: z.string().nullable(),
});

export const sectionSpecificDataSchema = z.object({
    /**
     * Vertical margin around each slot, on top of `columnGap`. Nothing edits it —
     * the header row has no spacing control and its columns have no settings
     * panel — so it only ever holds what a pre-versioning page left behind.
     */
    slotVerticalMargin: numberWithUnitSchema,
    columnGap: numberWithUnitSchema,
    padding: spacingSchema,
    /** Vertical alignment of the slots; the legacy `'T' | 'M' | 'B'` spelled out. */
    verticalPosition: z.enum(['top', 'middle', 'bottom']),
    slots: sectionSlotsSchema,
    background: sectionBackgroundSchema,
});

export const sectionSpecificSchema = sectionBaseSchema.extend({
    entitySubType: z.literal('specific'),
    props: sectionSpecificDataSchema,
    version: z.literal(0),
});

export const sectionSpecificV0Schema = sectionSpecificSchema;
export type SectionSpecificV0 = z.infer<typeof sectionSpecificV0Schema>;
